import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useConversationChannel } from '../composables/useConversationChannel'

export const useConversationsStore = defineStore('conversations', () => {
  // State
  const conversations = ref([])
  const currentConversation = ref(null)
  const messages = ref([])
  const loading = ref(false)
  const sending = ref(false)
  const status = ref('')
  const activeChannel = ref(null)
  const typingIndicatorType = ref('wave') // 'wave', 'pulse', 'shimmer'
  const retryDebounce = ref(new Map()) // Track retry timing
  const attachedFiles = ref([])
  const pendingFiles = ref([]) // For UI representation only not rly linked to a conversation in the backend
  const allFilesIds = ref([]) // To track all attached and pending file IDs
  const selectedFoundation = ref('gemini')
  // Getters
  const sortedConversations = computed(() => {
    return [...conversations.value].sort((a, b) =>
      new Date(b.updated_at) - new Date(a.updated_at)
    )
  })

  async function detachFile(fileId, conversationId) {
    console.log("Detaching file:", fileId, "from conversation:", conversationId)
    // if you found the file in pending files, just remove it locally
    const fileInPending = pendingFiles.value.some(f => f.id === fileId)

    if (fileInPending) {
      pendingFiles.value = pendingFiles.value.filter(f => f.id !== fileId)
      return
    }

    // Optimistically remove so the chip disappears immediately with no flicker.
    // Restore if the request fails.
    const removedFile = attachedFiles.value.find(f => f.id === fileId)
    attachedFiles.value = attachedFiles.value.filter(f => f.id !== fileId)

    const csrfToken = document.querySelector('[name="csrf-token"]')?.content
    const response = await fetch(
      `/api/conversations/${conversationId}/files/${fileId}`,
      {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken }
      }
    )

    if (!response.ok) {
      if (removedFile) attachedFiles.value = [ ...attachedFiles.value, removedFile ]
      throw new Error('Failed to detach file')
    }
  }

  const attachPendingFiles = async (conversationId) => {
    const targetConvId = conversationId || props.conversationId
    if (!targetConvId || pendingFiles.value.length === 0) return

    for (const file of pendingFiles.value) {
      try {
        const csrfToken = document.querySelector('[name="csrf-token"]')?.content
        const response = await fetch(`/api/conversations/${targetConvId}/files/attach`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({ file_id: file.id })
        })
        attachedFiles.value = [...attachedFiles.value, file]
        pendingFiles.value = pendingFiles.value.filter(f => f.id !== file.id)
      } catch (error) {
        console.error('Failed to attach file:', file.id, error)
      }
    }
    pendingFiles.value = []
  }
  // Actions
  async function fetchConversations() {
    loading.value = true
    try {
      const response = await fetch('/api/conversations', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      })
      if (response.ok) {
        conversations.value = await response.json()
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error)
    } finally {
      loading.value = false
    }
  }

  async function fetchConversation(id) {
    loading.value = true
    try {
      const response = await fetch(`/api/conversations/${id}`, {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      })
      if (response.ok) {
        const data = await response.json()
        console.log('Fetched conversation:', data)
        currentConversation.value = data.conversation
        messages.value = data.messages
        attachedFiles.value = data.files || []
        console.log("files from fetchConversation:", data.files)
        console.log('Attached files set to:', attachedFiles.value)
      } else if (response.status === 404) {
        // Throw error for archived/not found conversations
        throw new Error('Conversation not found')
      } else {
        throw new Error('Failed to fetch conversation')
      }
    } catch (error) {
      console.error('Failed to fetch conversation:', error)
      throw error // Re-throw so caller can handle it
    } finally {
      loading.value = false
    }
  }

  async function createConversation(title = 'New Conversation') {
    loading.value = true
    try {
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content
      const response = await fetch('/api/conversations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ conversation: { title } })
      })

      if (response.ok) {
        const data = await response.json()
        conversations.value.unshift(data)
        currentConversation.value = data
        messages.value = []
        return data
      }
    } catch (error) {
      console.error('Failed to create conversation:', error)
    } finally {
      loading.value = false
    }
  }

  function unsubscribeCurrent() {
    if (activeChannel.value) {
      activeChannel.value.unsubscribe()
      activeChannel.value = null
    }
    sending.value = false
    status.value = ''
  }

  // Subscribe to ActionCable for real-time updates
  function subscribeToConversation(conversationId) {
    // Unsubscribe from previous channel if any (no-op if already called early)
    if (activeChannel.value) {
      activeChannel.value.unsubscribe()
      activeChannel.value = null
    }

    // Reset sending state when switching conversations so the input isn't permanently disabled
    sending.value = false
    status.value = ''

    const channel = useConversationChannel(conversationId)
    channel.onMessage(handleActionCableMessage)
    channel.subscribe()
    activeChannel.value = channel

    // fetchConversation is always awaited before this runs, so messages is already populated.
    // If the last message is from the user with no assistant reply, the LLM is still processing.
    const lastMessage = messages.value[messages.value.length - 1]
    if (lastMessage && lastMessage.role === 'user') {
      sending.value = true
      status.value = 'Waiting for response...'
    }

    console.log(`Subscribed to conversation ${conversationId}`)
  }

  // Handle ActionCable messages
  function handleActionCableMessage(data) {
    console.log('[Store] Handling ActionCable message:', data)

    // Handle errors
    if (data.error) {
      status.value = ''
      sending.value = false
      console.error('ActionCable error:', data.message)
      return
    }

    // Conversation metadata changed (e.g. title set by LLM after first user message)
    if (data.type === 'conversation_updated' && data.conversation) {
      if (currentConversation.value && currentConversation.value.id === data.conversation.id) {
        currentConversation.value = { ...currentConversation.value, ...data.conversation }
      }
      const idx = conversations.value.findIndex(c => c.id === data.conversation.id)
      if (idx !== -1) {
        conversations.value[idx] = { ...conversations.value[idx], ...data.conversation }
      }
      return
    }

    // Message was saved to DB
    if (data.type === 'message_saved') {
      const existingIndex = messages.value.findIndex(m => m.id === data.message.id)
      if (existingIndex === -1) {
        messages.value.push(data.message)
      }
      status.value = ''
      sending.value = false
      return
    }

    // Insufficient credits
    if (data.type === 'insufficient_credits') {
      status.value = data.message
      sending.value = false
      return
    }

    // Streaming updates
    if (!data.is_final) {
      status.value = data.update_message || 'Processing...'
    } else {
      status.value = 'Complete'
      // Final response will be saved by backend, we'll get it via message_saved
    }
  }

  // Send message with optional file IDs
  async function sendMessage(content, conversationId, fileIds = []) {
    if (!content.trim()) return

    sending.value = true
    status.value = 'Sending...'

    // Optimistically add user message with pending status
    const tempId = `temp-${Date.now()}`
    const userMessage = {
      id: tempId,
      tempId: tempId,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
      status: 'sending',
      error: null,
      retryCount: 0,
      metadata: {}
    }
    messages.value.push(userMessage)

    try {
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content
      const response = await fetch(`/api/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          message: { content },
          file_ids: fileIds,
          foundation: selectedFoundation.value
        })
      })
      if (response.ok) {
        const data = await response.json()
        // Replace optimistic message with real one
        const index = messages.value.findIndex(m => m.id === userMessage.id)
        if (index !== -1) {
          messages.value[index] = { ...data.user_message, status: 'sent' }
        }
        status.value = 'Waiting for response...'
        // Assistant response will come via ActionCable
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || 'Failed to send message')
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      // Mark message as failed instead of removing it
      const index = messages.value.findIndex(m => m.id === userMessage.id)
      if (index !== -1) {
        messages.value[index] = {
          ...userMessage,
          status: 'failed',
          error: error.message
        }
      }
      sending.value = false
      status.value = ''
      // Don't throw - let the UI show the error
    }
  }

  function addMessage(message) {
    messages.value.push(message)
  }

  function clearCurrentConversation() {
    if (activeChannel.value) {
      activeChannel.value.unsubscribe()
      activeChannel.value = null
    }
    currentConversation.value = null
    messages.value = []
    status.value = ''
  }

  // Retry a failed message
  async function retryFailedMessage(messageId) {
    const message = messages.value.find(m => m.id === messageId)
    if (!message || message.status !== 'failed') return

    // Check retry limit
    if ((message.retryCount || 0) >= 3) {
      console.error('Max retry attempts reached')
      return
    }

    // Debounce retries
    const now = Date.now()
    const lastRetry = retryDebounce.value.get(messageId) || 0
    if (now - lastRetry < 500) return

    retryDebounce.value.set(messageId, now)

    // Update message status
    message.status = 'sending'
    message.error = null
    message.retryCount = (message.retryCount || 0) + 1

    sending.value = true
    status.value = 'Sending...'

    try {
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content
      const response = await fetch(`/api/conversations/${currentConversation.value.id}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          message: { content: message.content },
          foundation: selectedFoundation.value
        })
      })

      if (response.ok) {
        const data = await response.json()
        // Replace old message with new one
        const index = messages.value.findIndex(m => m.id === messageId)
        if (index !== -1) {
          messages.value[index] = {
            ...data.user_message,
            status: 'sent',
            retryCount: message.retryCount
          }
        }
        status.value = 'Waiting for response...'
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || 'Failed to send message')
      }
    } catch (error) {
      console.error('Retry failed:', error)
      message.status = 'failed'
      message.error = error.message
      sending.value = false
      status.value = ''
    }
  }

  // Update message feedback
  function updateMessageFeedback(messageId, feedbackValue, report = null) {
    const message = messages.value.find(m => m.id === messageId)
    if (!message) return

    if (!message.feedback) message.feedback = {}
    message.feedback.feedback = feedbackValue
    if (report !== null) message.feedback.report = report
  }

  // Get messages for LLM (exclude failed messages)
  function getMessagesForLLM() {
    return messages.value.filter(m => m.status !== 'failed')
  }

  return {
    // State
    conversations,
    currentConversation,
    messages,
    loading,
    sending,
    status,
    typingIndicatorType,
    retryDebounce,
    attachedFiles,
    pendingFiles,
    allFilesIds,
    selectedFoundation,
    // Getters
    sortedConversations,
    // Actions
    fetchConversations,
    fetchConversation,
    createConversation,
    sendMessage,
    addMessage,
    clearCurrentConversation,
    unsubscribeCurrent,
    subscribeToConversation,
    retryFailedMessage,
    updateMessageFeedback,
    getMessagesForLLM,
    detachFile,
    attachPendingFiles
  }
})
